import { markers } from './data';

// NEW: Use letter codes instead of numeric indices to avoid ambiguity
const BRAND_CODE = {
  'Arrtx': 'A',
  'Ohuhu': 'O',
  'Ohuhu (Old)': 'O',
  'Ohuhu (New)': 'O',
  'Decotime': 'D',
  'Tongfushop': 'T',
  'Touch': 'H',
  'TouchNew': 'N',
  'Shuttle Art': 'S',
  'Deli': 'E',
  'Chen Rui': 'C',
  'NOTE': 'M',
  'Pingo Doce': 'P',
  'Lidl': 'L',
  'Promarker': 'R',
  'CCHobby': 'B',
  'SFAIH': 'F',
  'Caliart': 'Y',
  'Bianyo': 'Z',
  'Hotu': 'G',
  'Calm Over Chaos': 'K',
  'Hangfan': 'Q',
  'Sanjoki': 'U',
  'Prism': 'V',
  'Royal & Langnickel': 'W',
  'Copic Sketch': 'I',
  'Copic Classic': 'J',
  'Copic Ciao': 'X'
};

// Reverse lookup: letter code to brand name (as stored in marker objects)
// For most brands, brand name == marker brand. Exceptions: Copic display names
// use "Copic - X" but markers store "Copic X" (no dash).
const CODE_TO_MARKER_BRAND = {
  'A': 'Arrtx',
  'O': 'Ohuhu',
  'D': 'Decotime',
  'T': 'Tongfushop',
  'H': 'Touch',
  'N': 'TouchNew',
  'S': 'Shuttle Art',
  'E': 'Deli',
  'C': 'Chen Rui',
  'M': 'NOTE',
  'P': 'Pingo Doce',
  'L': 'Lidl',
  'R': 'Promarker',
  'B': 'CCHobby',
  'F': 'SFAIH',
  'Y': 'Caliart',
  'Z': 'Bianyo',
  'G': 'Hotu',
  'K': 'Calm Over Chaos',
  'Q': 'Hangfan',
  'U': 'Sanjoki',
  'V': 'Prism',
  'W': 'Royal & Langnickel',
  'I': 'Copic Sketch',
  'J': 'Copic Classic',
  'X': 'Copic Ciao',
};

function sanitizeCode(input = '') {
  // Uppercase and strip non-alphanumeric to avoid `-` conflicts
  return String(input).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function encodePaletteForShare(palette = []) {
  const prefix = 'PC2'; // Changed to PC2 to indicate new format

  const tokens = (palette || []).map((m) => {
    const brandCode = BRAND_CODE[m.brand] ?? 'X'; // X = unknown brand fallback

    if (brandCode === 'O' || brandCode === 'U') {
      // Ohuhu/Sanjoki: {letter}{oldCode}_{newCode} or {letter}{oldCode} or {letter}_{newCode}
      const oldCode = sanitizeCode(m.code_old || '');
      const newCode = sanitizeCode(m.code_new || '');
      if (oldCode && newCode) return `${brandCode}${oldCode}_${newCode}`;
      if (oldCode) return `${brandCode}${oldCode}`;
      if (newCode) return `${brandCode}_${newCode}`;
      return brandCode;
    }

    const code = sanitizeCode(m.code || '');
    return `${brandCode}${code}`;
  });

  return `${prefix}-${tokens.join('-')}`;
}

// Decode PC2-style palette codes (new format with letter codes)
export function decodePaletteFromShare(code) {
  if (!code || typeof code !== 'string') {
    throw new Error('Empty palette code');
  }

  const trimmed = code.trim();

  // Support both old PC1 and new PC2 formats
  if (trimmed.startsWith('PC2-')) {
    return decodePalettePC2(trimmed);
  } else if (trimmed.startsWith('PC1-')) {
    return decodePalettePC1(trimmed);
  } else {
    throw new Error('Invalid code format');
  }
}

// NEW: Decode PC2 format (with letter brand codes)
function decodePalettePC2(code) {
  const normalize = (s) => (s ? String(s).toUpperCase().replace(/[^A-Z0-9]/g, '') : '');

  // Precompute lookup maps
  const byBrandAndCode = new Map();
  const oldNewBrandLists = { O: [], U: [] }; // brands using code_old/code_new

  for (const m of markers) {
    if (m.brand === 'Ohuhu') {
      oldNewBrandLists.O.push({ ...m, _n_old: normalize(m.code_old), _n_new: normalize(m.code_new) });
    } else if (m.brand === 'Sanjoki') {
      oldNewBrandLists.U.push({ ...m, _n_old: normalize(m.code_old), _n_new: normalize(m.code_new) });
    } else {
      const key = `${m.brand}|${normalize(m.code)}`;
      if (!byBrandAndCode.has(key)) byBrandAndCode.set(key, m);
    }
  }

  const tokens = code.split('-').slice(1); // drop PC2
  if (tokens.length === 0) throw new Error('No markers in code');

  const result = [];
  for (const token of tokens) {
    if (!token) continue;

    const brandCode = token[0];
    if (!/^[A-Z]$/.test(brandCode)) throw new Error('Malformed token: expected letter brand code');

    const rest = token.slice(1);

    if (brandCode === 'O' || brandCode === 'U') {
      // Ohuhu/Sanjoki: use code_old/code_new lookup
      const list = oldNewBrandLists[brandCode];
      const [oldRaw, newRaw] = rest.split('_');
      const nOld = normalize(oldRaw);
      const nNew = normalize(newRaw);

      let found = null;
      if (nOld && nNew) {
        found = list.find((m) => m._n_old === nOld && m._n_new === nNew);
      }
      if (!found && nOld) {
        found = list.find((m) => m._n_old === nOld);
      }
      if (!found && nNew) {
        found = list.find((m) => m._n_new === nNew);
      }
      if (!found) throw new Error(`Unknown ${brandCode === 'O' ? 'Ohuhu' : 'Sanjoki'} code: ${rest}`);
      result.push(found);
      continue;
    }

    const brand = CODE_TO_MARKER_BRAND[brandCode];
    if (!brand) throw new Error(`Unknown brand code: ${brandCode}`);

    const key = `${brand}|${normalize(rest)}`;
    const found = byBrandAndCode.get(key);
    if (!found) throw new Error(`Unknown code for ${brand}: ${rest}`);

    result.push(found);
  }

  return result;
}

// OLD: Keep PC1 decoder for backward compatibility
function decodePalettePC1(code) {
  const normalize = (s) => (s ? String(s).toUpperCase().replace(/[^A-Z0-9]/g, '') : '');

  const byBrandAndCode = new Map();
  const ohuhuList = [];
  for (const m of markers) {
    if (m.brand === 'Ohuhu') {
      ohuhuList.push({
        ...m,
        _n_old: normalize(m.code_old),
        _n_new: normalize(m.code_new),
      });
    } else {
      const key = `${m.brand}|${normalize(m.code)}`;
      if (!byBrandAndCode.has(key)) byBrandAndCode.set(key, m);
    }
  }

  const brandByIndex = {
    1: 'Decotime',
    2: 'Tongfushop',
    3: 'Touch',
    4: 'TouchNew',
    5: 'Shuttle Art',
    6: 'Deli',
    7: 'Chen Rui',
    8: 'NOTE',
    9: 'Pingo Doce',
    10: 'Lidl',
    11: 'Promarker',
    12: 'CCHobby',
    99: 'Unknown'
  };

  const tokens = code.split('-').slice(1); // drop PC1
  if (tokens.length === 0) throw new Error('No markers in code');

  const result = [];
  for (const token of tokens) {
    if (!token) continue;
    const idxChar = token[0];
    if (!/^[0-9]$/.test(idxChar)) throw new Error('Malformed token');
    const idx = Number(idxChar);
    const rest = token.slice(1);

    if (idx === 0) {
      const [oldRaw, newRaw] = rest.split('_');
      const nOld = normalize(oldRaw);
      const nNew = normalize(newRaw);

      let found = null;
      if (nOld && nNew) {
        found = ohuhuList.find((m) => m._n_old === nOld && m._n_new === nNew);
      }
      if (!found && nOld) {
        found = ohuhuList.find((m) => m._n_old === nOld);
      }
      if (!found && nNew) {
        found = ohuhuList.find((m) => m._n_new === nNew);
      }
      if (!found) throw new Error('Unknown Ohuhu code');
      result.push(found);
      continue;
    }

    const brand = brandByIndex[idx];
    if (!brand) throw new Error('Unknown brand index');
    const key = `${brand}|${normalize(rest)}`;
    const found = byBrandAndCode.get(key);
    if (!found) throw new Error(`Unknown code for ${brand}`);
    result.push(found);
  }

  return result;
}
